import { useEffect, useState, useCallback } from "react";
import { Lightning, Trash, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

const EVENT_LABELS = {
  "post.published": "Post published",
  "post.scheduled": "Post scheduled",
  "content.repurposed": "Pack repurposed",
  "payment.succeeded": "Payment received",
};

export function WebhooksPanel({ activeId }) {
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [url, setUrl] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeId) return;
    const { data } = await api.get(`/webhooks?workspace_id=${activeId}`);
    setHooks(data.webhooks || []);
    setEvents(data.events || []);
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (e) => setSelected((s) => (s.includes(e) ? s.filter((x) => x !== e) : [...s, e]));

  const add = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await api.post("/webhooks", { workspace_id: activeId, url: url.trim(), events: selected });
      setUrl(""); setSelected([]);
      await load();
      toast.success("Webhook added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add webhook");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/webhooks/${id}`);
      setHooks((h) => h.filter((x) => x.id !== id));
      toast.success("Webhook removed");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-12" data-testid="webhooks-panel">
      <div className="flex items-center gap-2">
        <Lightning size={22} weight="fill" className="text-primary" />
        <h2 className="font-heading font-bold text-xl">Zapier & Webhooks</h2>
      </div>
      <p className="text-sm text-muted-foreground mt-2">
        Paste a Zapier "Catch Hook" URL (or any endpoint). We'll POST a JSON payload whenever the selected events fire — pipe your posts into 5,000+ apps.
      </p>

      <div className="mt-6 rounded-lg border border-border bg-card p-5 space-y-4">
        <Input data-testid="webhook-url-input" placeholder="https://hooks.zapier.com/hooks/catch/..." value={url}
          onChange={(e) => setUrl(e.target.value)} />
        <div className="flex flex-wrap gap-2">
          {events.map((e) => (
            <button key={e} type="button" data-testid={`webhook-event-${e}`} onClick={() => toggle(e)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selected.includes(e) ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent"}`}>
              {EVENT_LABELS[e] || e}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">No events selected = fires on all events.</p>
        <Button data-testid="webhook-add-btn" onClick={add} disabled={busy || !url.trim()}>
          <Plus size={16} className="mr-1.5" /> Add webhook
        </Button>
      </div>

      {hooks.length > 0 && (
        <div className="mt-4 space-y-3">
          {hooks.map((h) => (
            <div key={h.id} data-testid={`webhook-${h.id}`} className="rounded-lg border border-border bg-card p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-mono truncate">{h.url}</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {h.events.map((e) => <Badge key={e} variant="secondary" className="text-xs">{EVENT_LABELS[e] || e}</Badge>)}
                </div>
              </div>
              <Button variant="ghost" size="icon" data-testid={`webhook-delete-${h.id}`} onClick={() => remove(h.id)}
                className="text-muted-foreground hover:text-destructive">
                <Trash size={16} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
